// Huddle's website-template options (DIRECTION.md §9, amended 2026-10-08).
//
// Must deep-equal backend/src/services/site-templates/templates/huddle.ts
// (test/site/contract/options.sync.test.ts, which compares the English text).
// Pure data: the eager manifest and the lazy template both import it, so keep
// it tiny. The text is admin-facing: selectors on the admin catalogue
// (siteTemplates.huddle), shown in the admin's language (#1063).

import type { OptionsSchema } from '../../core/template/options'

export const HUDDLE_OPTIONS = [
  {
    key: 'surface',
    type: 'enum',
    role: 'surface',
    autoMap: { light: 'paper', dark: 'night' },
    label: (m) => m.siteTemplates.huddle.options.surface.label,
    help: (m) => m.siteTemplates.huddle.options.surface.help,
    default: 'auto',
    values: [
      { value: 'auto', label: (m) => m.siteTemplates.huddle.options.surface.auto },
      { value: 'paper', label: (m) => m.siteTemplates.huddle.options.surface.paper },
      { value: 'white', label: (m) => m.siteTemplates.huddle.options.surface.white },
      { value: 'night', label: (m) => m.siteTemplates.huddle.options.surface.night },
    ],
  },
  {
    key: 'accentStrength',
    type: 'enum',
    label: (m) => m.siteTemplates.huddle.options.accentStrength.label,
    help: (m) => m.siteTemplates.huddle.options.accentStrength.help,
    default: 'bold',
    values: [
      { value: 'bold', label: (m) => m.siteTemplates.huddle.options.accentStrength.bold },
      { value: 'quiet', label: (m) => m.siteTemplates.huddle.options.accentStrength.quiet },
    ],
  },
  {
    key: 'startPage',
    type: 'enum',
    label: (m) => m.siteTemplates.huddle.options.startPage.label,
    help: (m) => m.siteTemplates.huddle.options.startPage.help,
    default: 'myTeam',
    values: [
      { value: 'myTeam', label: (m) => m.siteTemplates.huddle.options.startPage.myTeam },
      { value: 'league', label: (m) => m.siteTemplates.huddle.options.startPage.league },
    ],
  },
  {
    key: 'homeSpotlight',
    type: 'enum',
    label: (m) => m.siteTemplates.huddle.options.homeSpotlight.label,
    help: (m) => m.siteTemplates.huddle.options.homeSpotlight.help,
    default: 'auto',
    values: [
      { value: 'auto', label: (m) => m.siteTemplates.huddle.options.homeSpotlight.auto },
      { value: 'banner', label: (m) => m.siteTemplates.huddle.options.homeSpotlight.banner },
      { value: 'off', label: (m) => m.siteTemplates.huddle.options.homeSpotlight.off },
    ],
  },
  {
    key: 'fourthTab',
    type: 'enum',
    label: (m) => m.siteTemplates.huddle.options.fourthTab.label,
    help: (m) => m.siteTemplates.huddle.options.fourthTab.help,
    default: 'standings',
    values: [
      { value: 'standings', label: (m) => m.siteTemplates.huddle.options.fourthTab.standings },
      { value: 'signup', label: (m) => m.siteTemplates.huddle.options.fourthTab.signup },
      { value: 'tickets', label: (m) => m.siteTemplates.huddle.options.fourthTab.tickets },
    ],
  },
  {
    key: 'teamColours',
    type: 'boolean',
    label: (m) => m.siteTemplates.huddle.options.teamColours.label,
    help: (m) => m.siteTemplates.huddle.options.teamColours.help,
    default: true,
  },
] as const satisfies OptionsSchema

export type HuddleSurface = 'paper' | 'white' | 'night'

/** Resolved option values, as the template reads them. */
export interface HuddleOptions {
  surface: 'auto' | HuddleSurface
  accentStrength: 'bold' | 'quiet'
  startPage: 'myTeam' | 'league'
  homeSpotlight: 'auto' | 'banner' | 'off'
  fourthTab: 'standings' | 'signup' | 'tickets'
  teamColours: boolean
}

export const HUDDLE_DEFAULTS: HuddleOptions = {
  surface: 'auto',
  accentStrength: 'bold',
  startPage: 'myTeam',
  homeSpotlight: 'auto',
  fourthTab: 'standings',
  teamColours: true,
}
